/**
 * Web Bluetooth BLE client for thermal printers (any BLE printer).
 * Connect/reconnect must be triggered by user gesture (button click).
 * Discovery uses acceptAllDevices so any printer name is supported.
 */

import type { PhomemoConnectionState } from './types';

/** Phomemo printer service/characteristic (most common for Phomemo D520BT, etc.) */
const PHOMEMO_SERVICE_UUID = '0000ff00-0000-1000-8000-00805f9b34fb';
const PHOMEMO_WRITE_UUID = '0000ff02-0000-1000-8000-00805f9b34fb';
/** Generic thermal printer service/characteristic (many printers) */
const SERVICE_UUID = '0000ffe0-0000-1000-8000-00805f9b34fb';
const CHAR_UUID = '0000ffe1-0000-1000-8000-00805f9b34fb';
/** Nordic UART Service (NUS) – many BLE serial/thermal printers use this; required in optionalServices for access. */
const NUS_SERVICE_UUID = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const NUS_RX_CHAR_UUID = '6e400002-b5a3-f393-e0a9-e50e24dcca9e';

/** Common BLE service UUIDs – must list every service we may need; browser only grants access to these. */
const COMMON_SERVICES: string[] = [
  '0000ff00-0000-1000-8000-00805f9b34fb', // Phomemo
  '0000ffe0-0000-1000-8000-00805f9b34fb', // Many printers
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART
  '00001800-0000-1000-8000-00805f9b34fb',
  '00001801-0000-1000-8000-00805f9b34fb',
];

const REQUEST_DEVICE_TIMEOUT_MS = 45000;

function getFriendlyBluetoothError(err: unknown): string {
  if (err instanceof DOMException) {
    switch (err.name) {
      case 'NotFoundError':
        return 'No device selected or no devices found.';
      case 'SecurityError':
        return 'Bluetooth access was denied or is not allowed in this context.';
      default:
        return err.message || 'Bluetooth error';
    }
  }
  if (err instanceof Error) return err.message;
  return 'Connection failed';
}

interface WebBleState {
  device: BluetoothDevice | null;
  gatt: BluetoothRemoteGATTServer | null;
  characteristic: BluetoothRemoteGATTCharacteristic | null;
  state: PhomemoConnectionState;
  deviceName: string | undefined;
}

const state: WebBleState = {
  device: null,
  gatt: null,
  characteristic: null,
  state: 'disconnected',
  deviceName: undefined,
};

let connectionListener: ((s: PhomemoConnectionState, name?: string) => void) | null = null;
let disconnectHandler: (() => void) | null = null;

function notify(s: PhomemoConnectionState, name?: string) {
  state.state = s;
  state.deviceName = name;
  connectionListener?.(s, name);
}

function clearRefs() {
  state.device = null;
  state.gatt = null;
  state.characteristic = null;
  state.deviceName = undefined;
}

export function isPhomemoAvailable(): boolean {
  if (typeof navigator === 'undefined') return false;
  return !!navigator.bluetooth;
}

export function isSecureContext(): boolean {
  return typeof window !== 'undefined' && window.isSecureContext === true;
}

export async function getConnectionState(): Promise<{
  state: PhomemoConnectionState;
  deviceName?: string;
}> {
  return { state: state.state, deviceName: state.deviceName };
}

export function onConnectionChange(cb: (state: PhomemoConnectionState, deviceName?: string) => void): () => void {
  connectionListener = cb;
  return () => { connectionListener = null; };
}

function findWritableCharacteristic(server: BluetoothRemoteGATTServer): Promise<BluetoothRemoteGATTCharacteristic | null> {
  return server.getPrimaryServices().then(async (services) => {
    for (const svc of services) {
      const chars = await svc.getCharacteristics();
      for (const c of chars) {
        if (c.properties.write || c.properties.writeWithoutResponse) return c;
      }
    }
    return null;
  });
}

export type PrinterMode = 'phomemo' | 'generic' | 'any';

export interface ConnectOptions {
  /** When true (default), scan all services for a writable characteristic if known pairs fail. */
  autoSelectWritableCharacteristic?: boolean;
  /** Printer mode: 'phomemo' filters by FF00 service, 'generic' uses FFE0/NUS, 'any' accepts all devices. Default: 'phomemo'. */
  printerMode?: PrinterMode;
}

/** Try known service/char pairs first (0xFF00/0xFF02 Phomemo, 0xFFE0/0xFFE1, NUS); if useAutoSelect, then scan all services. */
async function getWritableCharacteristic(
  gatt: BluetoothRemoteGATTServer,
  useAutoSelect: boolean = true
): Promise<BluetoothRemoteGATTCharacteristic | null> {
  try {
    // Try Phomemo service/characteristic first (0xFF00/0xFF02)
    const svcPhomemo = await gatt.getPrimaryService(PHOMEMO_SERVICE_UUID).catch(() => null);
    if (svcPhomemo) {
      const c = await svcPhomemo.getCharacteristic(PHOMEMO_WRITE_UUID).catch(() => null);
      if (c && (c.properties.write || c.properties.writeWithoutResponse)) return c;
    }
    // Try generic 0xFFE0/0xFFE1
    const svc0 = await gatt.getPrimaryService(SERVICE_UUID).catch(() => null);
    if (svc0) {
      const c = await svc0.getCharacteristic(CHAR_UUID).catch(() => null);
      if (c && (c.properties.write || c.properties.writeWithoutResponse)) return c;
    }
    // Try Nordic UART Service
    const svcNus = await gatt.getPrimaryService(NUS_SERVICE_UUID).catch(() => null);
    if (svcNus) {
      const c = await svcNus.getCharacteristic(NUS_RX_CHAR_UUID).catch(() => null);
      if (c && (c.properties.write || c.properties.writeWithoutResponse)) return c;
    }
  } catch {
    // fall through to scan when useAutoSelect
  }
  if (!useAutoSelect) return null;
  return findWritableCharacteristic(gatt);
}

/**
 * Get writable characteristic based on printer mode.
 * Phomemo mode: strict FF00/FF02, no fallback (shows clear bonding/busy errors).
 * Other modes: try known pairs, then scan if autoSelect enabled.
 */
async function getCharacteristicForMode(
  gatt: BluetoothRemoteGATTServer,
  printerMode: PrinterMode,
  autoSelect: boolean
): Promise<BluetoothRemoteGATTCharacteristic> {
  const visibleServices = await gatt.getPrimaryServices().then(svcs => svcs.map(s => s.uuid)).catch(() => []);
  console.log('[BLE] Visible services:', visibleServices);

  if (printerMode === 'phomemo') {
    // Strict Phomemo mode: NO fallback scanning (hides real issues)
    const hasFF00 = visibleServices.includes(PHOMEMO_SERVICE_UUID);
    
    if (!hasFF00) {
      throw new Error(
        'Phomemo service FF00 is not visible to Chrome. ' +
        'Fix: Android Settings → Bluetooth → Forget/unpair the printer, ' +
        'turn Bluetooth off/on, then connect ONLY from this app (do not OS-pair).'
      );
    }

    // Service is visible, try to open FF02 characteristic
    try {
      const svc = await gatt.getPrimaryService(PHOMEMO_SERVICE_UUID);
      return await svc.getCharacteristic(PHOMEMO_WRITE_UUID);
    } catch (e) {
      throw new Error(
        'Printer is reachable but the write characteristic FF02 could not be opened. ' +
        'Common cause: printer is connected to another phone/app (Labelife) or another tab. ' +
        'Solution: Close other apps, disable Bluetooth on other device, power-cycle printer, then retry.'
      );
    }
  } else {
    // Generic/any mode: use fallback scanning
    const char = await getWritableCharacteristic(gatt, autoSelect);
    if (!char) {
      throw new Error('No writable characteristic found. This device may not be a supported printer.');
    }
    return char;
  }
}

/**
 * Connect via requestDevice (user gesture required).
 * Filters by service UUID for reliable connection (name can vary).
 * Wraps requestDevice in a timeout to avoid endless "Connecting...".
 */
export async function connect(options?: ConnectOptions): Promise<{ deviceId: string; deviceName?: string }> {
  const autoSelect = options?.autoSelectWritableCharacteristic !== false;
  const printerMode = options?.printerMode ?? 'phomemo';
  if (!navigator.bluetooth) throw new Error('Web Bluetooth not available');
  notify('connecting');

  let device: BluetoothDevice;

  try {
    let requestPromise: Promise<BluetoothDevice>;
    
    if (printerMode === 'phomemo') {
      // Filter by Phomemo service FF00 - reliable regardless of device name
      requestPromise = navigator.bluetooth.requestDevice({
        filters: [{ services: [PHOMEMO_SERVICE_UUID] }],
        optionalServices: [PHOMEMO_SERVICE_UUID],
      });
    } else if (printerMode === 'generic') {
      // Filter by common thermal printer services (FFE0, NUS)
      requestPromise = navigator.bluetooth.requestDevice({
        filters: [
          { services: [SERVICE_UUID] },
          { services: [NUS_SERVICE_UUID] },
        ],
        optionalServices: COMMON_SERVICES,
      });
    } else {
      // 'any' mode: accept all devices (fallback for unknown printers)
      requestPromise = navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: COMMON_SERVICES,
      });
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(
        () => reject(new Error('Connection timed out. Ensure the printer is on and in pairing mode, then try again.')),
        REQUEST_DEVICE_TIMEOUT_MS
      );
    });
    device = await Promise.race([requestPromise, timeoutPromise]);
  } catch (e) {
    notify('disconnected');
    const message = e instanceof Error && e.message.startsWith('Connection timed out')
      ? e.message
      : getFriendlyBluetoothError(e);
    throw new Error(message);
  }

  const gatt = device.gatt;
  if (!gatt) {
    notify('disconnected');
    throw new Error('GATT not available');
  }

  // Ensure clean disconnect before connecting (helps with Phomemo reconnect issues)
  // Use device.gatt reference consistently (some Android builds behave weirdly with gatt alone)
  if (device.gatt?.connected) {
    try { 
      device.gatt.disconnect(); 
    } catch {}
  }
  // Longer delay for Phomemo to ensure firmware is ready
  await new Promise(r => setTimeout(r, printerMode === 'phomemo' ? 600 : 200));

  try {
    await gatt.connect();
  } catch (e) {
    notify('disconnected');
    throw new Error(getFriendlyBluetoothError(e));
  }

  let char: BluetoothRemoteGATTCharacteristic;
  try {
    char = await getCharacteristicForMode(gatt, printerMode, autoSelect);
  } catch (e) {
    gatt.disconnect();
    clearRefs();
    notify('disconnected');
    throw e;
  }

  // Remove old disconnect handler to avoid duplicate listeners
  if (disconnectHandler && state.device) {
    state.device.removeEventListener('gattserverdisconnected', disconnectHandler as any);
  }

  disconnectHandler = () => {
    clearRefs();
    notify('disconnected');
  };
  device.addEventListener('gattserverdisconnected', disconnectHandler as any);

  state.device = device;
  state.gatt = gatt;
  state.characteristic = char;
  state.deviceName = device.name ?? undefined;
  notify('connected', state.deviceName);

  return { deviceId: device.id, deviceName: state.deviceName };
}

/**
 * Reconnect (user gesture required).
 * Uses getDevices() when available; else requestDevice().
 */
export async function reconnect(savedDeviceId: string, options?: ConnectOptions): Promise<{ deviceId: string; deviceName?: string }> {
  if (!navigator.bluetooth) throw new Error('Web Bluetooth not available');
  const autoSelect = options?.autoSelectWritableCharacteristic !== false;
  const printerMode = options?.printerMode ?? 'phomemo';

  const getDevices = (navigator.bluetooth as { getDevices?: () => Promise<BluetoothDevice[]> }).getDevices;
  if (typeof getDevices === 'function') {
    const devices = await getDevices();
    const device = devices.find((d) => d.id === savedDeviceId);
    if (device) {
      notify('connecting');
      const gatt = device.gatt;
      if (!gatt) throw new Error('GATT not available');
      
      // Ensure clean disconnect before reconnecting
      // Use device.gatt reference consistently (some Android builds behave weirdly with gatt alone)
      if (device.gatt?.connected) {
        try { 
          device.gatt.disconnect(); 
        } catch {}
      }
      // Longer delay for Phomemo to ensure firmware is ready
      await new Promise(r => setTimeout(r, printerMode === 'phomemo' ? 600 : 200));
      
      try {
        await gatt.connect();
      } catch (e) {
        notify('disconnected');
        throw new Error(getFriendlyBluetoothError(e));
      }

      // For Phomemo: check if FF00 is accessible (cached permissions may not include it)
      if (printerMode === 'phomemo') {
        const visible = await gatt.getPrimaryServices().then(s => s.map(x => x.uuid)).catch(() => []);
        if (!visible.includes(PHOMEMO_SERVICE_UUID)) {
          gatt.disconnect();
          notify('disconnected');
          throw new Error(
            'Need re-authorization. Chrome does not have permission for Phomemo service (FF00) in cached reconnect. ' +
            'Tap Connect and select the printer again to re-grant permissions.'
          );
        }
      }

      let char: BluetoothRemoteGATTCharacteristic;
      try {
        char = await getCharacteristicForMode(gatt, printerMode, autoSelect);
      } catch (e) {
        gatt.disconnect();
        clearRefs();
        notify('disconnected');
        throw e;
      }

      // Remove old disconnect handler to avoid duplicate listeners
      if (disconnectHandler && state.device) {
        state.device.removeEventListener('gattserverdisconnected', disconnectHandler as any);
      }

      disconnectHandler = () => {
        clearRefs();
        notify('disconnected');
      };
      device.addEventListener('gattserverdisconnected', disconnectHandler as any);

      state.device = device;
      state.gatt = gatt;
      state.characteristic = char;
      state.deviceName = device.name ?? undefined;
      notify('connected', state.deviceName);
      return { deviceId: device.id, deviceName: state.deviceName };
    }
  }

  return connect(options);
}

/**
 * Silent reconnect using only cached devices (getDevices). Never calls requestDevice().
 * Use on page load when we have a saved deviceId; no user gesture required (Chrome 87+).
 * Returns true if reconnected, false otherwise (caller should show Reconnect button).
 */
export async function tryReconnectFromCache(savedDeviceId: string, options?: ConnectOptions): Promise<boolean> {
  if (!navigator.bluetooth) return false;
  const getDevices = (navigator.bluetooth as { getDevices?: () => Promise<BluetoothDevice[]> }).getDevices;
  if (typeof getDevices !== 'function') return false;
  const autoSelect = options?.autoSelectWritableCharacteristic !== false;
  const printerMode = options?.printerMode ?? 'phomemo';

  try {
    const devices = await getDevices();
    const device = devices.find((d) => d.id === savedDeviceId);
    if (!device) return false;

    notify('connecting');
    const gatt = device.gatt;
    if (!gatt) {
      notify('disconnected');
      return false;
    }

    // Ensure clean disconnect before reconnecting
    // Use device.gatt reference consistently (some Android builds behave weirdly with gatt alone)
    if (device.gatt?.connected) {
      try { 
        device.gatt.disconnect(); 
      } catch {}
    }
    // Longer delay for Phomemo to ensure firmware is ready
    await new Promise(r => setTimeout(r, printerMode === 'phomemo' ? 600 : 200));

    await gatt.connect();

    // For Phomemo: check if FF00 is accessible (cached permissions may not include it)
    if (printerMode === 'phomemo') {
      const visible = await gatt.getPrimaryServices().then(s => s.map(x => x.uuid)).catch(() => []);
      if (!visible.includes(PHOMEMO_SERVICE_UUID)) {
        // Chrome doesn't have permission for FF00 in cached reconnect → need user gesture
        gatt.disconnect();
        notify('disconnected');
        return false; // Caller should show "Connect" button
      }
    }

    const char = await getCharacteristicForMode(gatt, printerMode, autoSelect);
    
    // Remove old disconnect handler to avoid duplicate listeners
    if (disconnectHandler && state.device) {
      state.device.removeEventListener('gattserverdisconnected', disconnectHandler as any);
    }

    disconnectHandler = () => {
      clearRefs();
      notify('disconnected');
    };
    device.addEventListener('gattserverdisconnected', disconnectHandler as any);

    state.device = device;
    state.gatt = gatt;
    state.characteristic = char;
    state.deviceName = device.name ?? undefined;
    notify('connected', state.deviceName);
    return true;
  } catch {
    notify('disconnected');
    return false;
  }
}

export async function disconnect(): Promise<void> {
  if (state.gatt?.connected) state.gatt.disconnect();
  clearRefs();
  notify('disconnected');
}

export async function writeChunk(data: Uint8Array): Promise<void> {
  const c = state.characteristic;
  if (!c) throw new Error('Printer disconnected. Please reconnect and try again.');
  if (c.properties.writeWithoutResponse) {
    await c.writeValueWithoutResponse(data);
  } else {
    await c.writeValue(data);
  }
}

/**
 * Write large data (e.g. shipping label bitmap) in chunks with delays.
 * BLE requires chunking for reliable transmission of tens of KB.
 * @param data Full data buffer to write
 * @param chunkSize Bytes per chunk (default 180 for BLE MTU compatibility)
 * @param delayMs Delay between chunks (default 10ms)
 */
export async function writeAll(data: Uint8Array, chunkSize: number = 180, delayMs: number = 10): Promise<void> {
  for (let i = 0; i < data.length; i += chunkSize) {
    const chunk = data.slice(i, i + chunkSize);
    await writeChunk(chunk);
    await new Promise(r => setTimeout(r, delayMs));
  }
}
